import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, Play, Users, ClipboardList, FileText, Timer, ArrowRight, Smile, Frown, CloudRain, Wind, BookOpen, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { IconRenderer } from "../lib/icons";
import Header from "../components/Header";
import UserNav from "../components/UserNav";

const COLOR_BG = { teal: "#38B2AC", lavender: "#9F7AEA", peach: "#ED8936", yellow: "#ECC94B" };

export default function UserHome() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(localStorage.getItem("rb_profile") || "");
  const [routines, setRoutines] = useState([]);
  const [boards, setBoards] = useState([]);
  const [choices, setChoices] = useState([]);
  const [firstThen, setFirstThen] = useState(null);
  const [todayMood, setTodayMood] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadContent = useCallback(async (pid) => {
    const params = pid ? { profile_id: pid } : {};
    const [r, b, c] = await Promise.all([
      api.get("/routines", { params }),
      api.get("/boards", { params }),
      api.get("/choices", { params }),
    ]);
    setRoutines(r.data);
    setBoards(b.data);
    setChoices(c.data);
    if (pid) {
      try {
        const [ft, md] = await Promise.all([
          api.get("/firstthen", { params: { profile_id: pid } }),
          api.get("/mood", { params: { profile_id: pid, days: 1 } }),
        ]);
        setFirstThen(ft.data.first_label ? ft.data : null);
        setTodayMood(md.data.moods[0]?.mood || null);
      } catch { /* optional */ }
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profiles");
        setProfiles(data);
        const stored = localStorage.getItem("rb_profile");
        const pid = data.find((p) => p.id === stored)?.id || data[0]?.id || "";
        setActiveProfile(pid);
        if (pid) localStorage.setItem("rb_profile", pid);
        await loadContent(pid);
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [loadContent]);

  const switchProfile = async (pid) => {
    setActiveProfile(pid);
    localStorage.setItem("rb_profile", pid);
    await loadContent(pid);
  };

  const current = profiles.find((p) => p.id === activeProfile);

  const logMood = async (mood) => {
    setTodayMood(mood);
    try {
      await api.post("/mood", { profile_id: activeProfile, mood });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const MOODS = [
    { id: "happy", label: "Happy", Icon: Smile, cls: "border-[#48BB78] bg-[#48BB78]/10 text-[#2F855A]" },
    { id: "okay", label: "Okay", Icon: CloudRain, cls: "border-[#ECC94B] bg-[#ECC94B]/15 text-[#975A16]" },
    { id: "sad", label: "Sad", Icon: Frown, cls: "border-[#4299E1] bg-[#4299E1]/10 text-[#2B6CB0]" },
  ];

  return (
    <div data-testid="user-home" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8 pb-32">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
          Hello{current ? `, ${current.name}` : ""}!
        </h1>
        <p className="mt-2 text-lg text-muted-foreground">What would you like to do?</p>

        {profiles.length > 1 && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Users size={24} strokeWidth={2.5} className="text-muted-foreground" />
            {profiles.map((p) => (
              <button
                key={p.id}
                data-testid={`user-profile-select-${p.id}`}
                onClick={() => switchProfile(p.id)}
                className={`min-h-[52px] px-5 rounded-full border-2 font-bold text-base flex items-center gap-2 transition-colors duration-200 ${
                  p.id === activeProfile ? "border-primary bg-primary/10" : "border-foreground/20 bg-white"
                }`}
              >
                <span className="w-4 h-4 rounded-full" style={{ background: COLOR_BG[p.color] || COLOR_BG.teal }} />
                {p.name}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="mt-10 text-lg text-muted-foreground">Loading…</p>
        ) : (
          <div className="mt-8 space-y-5">
            <div data-testid="feelings-checkin" className="bg-white rounded-3xl border-2 border-foreground/10 p-5">
              <p className="text-xl font-extrabold mb-3">How are you feeling?</p>
              <div className="grid grid-cols-3 gap-3">
                {MOODS.map(({ id, label, Icon, cls }) => (
                  <button
                    key={id}
                    data-testid={`mood-${id}`}
                    onClick={() => logMood(id)}
                    aria-pressed={todayMood === id}
                    aria-label={`I am feeling ${label}`}
                    className={`min-h-[88px] rounded-3xl border-2 flex flex-col items-center justify-center gap-1 font-extrabold text-lg transition-transform duration-200 ease-out active:scale-95 ${cls} ${todayMood === id ? "ring-4 ring-primary/30" : ""}`}
                  >
                    <Icon size={36} strokeWidth={2.5} />
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {routines.map((r) => (
              <button
                key={r.id}
                data-testid={`start-routine-${r.id}`}
                onClick={() => navigate(`/use/routine/${r.id}`)}
                className="w-full bg-white rounded-3xl border-2 border-foreground/10 p-6 flex items-center gap-5 text-left transition-transform duration-200 ease-out hover:-translate-y-px"
              >
                <span className="w-20 h-20 rounded-2xl bg-primary/10 grid place-items-center shrink-0">
                  <IconRenderer name={r.icon} size={44} className="text-primary" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-2xl font-extrabold truncate">{r.name}</span>
                  <span className="block text-lg text-muted-foreground font-semibold">{r.steps.length} steps</span>
                </span>
                <span className="min-h-[56px] px-6 rounded-full bg-primary text-white font-bold text-lg flex items-center gap-2 shrink-0">
                  <Play size={22} strokeWidth={3} /> Start
                </span>
              </button>
            ))}

            <button
              data-testid="open-calm-card"
              onClick={() => navigate("/use/calm")}
              className="w-full bg-[#0A192F] rounded-3xl border-2 border-[#38B2AC]/50 p-6 flex items-center gap-5 text-left transition-transform duration-200 ease-out hover:-translate-y-px"
            >
              <span className="w-20 h-20 rounded-2xl bg-[#38B2AC]/15 grid place-items-center shrink-0">
                <Wind size={44} strokeWidth={2.5} className="text-[#38B2AC]" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-2xl font-extrabold text-white">Calm down</span>
                <span className="block text-lg text-white/60 font-semibold">Breathe with the circle</span>
              </span>
              <span className="min-h-[56px] px-6 rounded-full bg-[#38B2AC] text-white font-bold text-lg flex items-center shrink-0">Start</span>
            </button>

            <button
              data-testid="open-stories-card"
              onClick={() => navigate("/use/stories")}
              className="w-full bg-white rounded-3xl border-2 border-[#9F7AEA]/50 p-6 flex items-center gap-5 text-left transition-transform duration-200 ease-out hover:-translate-y-px"
            >
              <span className="w-20 h-20 rounded-2xl bg-[#9F7AEA]/10 grid place-items-center shrink-0">
                <BookOpen size={44} strokeWidth={2.5} className="text-[#9F7AEA]" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-2xl font-extrabold">My stories</span>
                <span className="block text-lg text-muted-foreground font-semibold">Stories about new things</span>
              </span>
              <span className="min-h-[56px] px-6 rounded-full bg-[#9F7AEA] text-white font-bold text-lg flex items-center shrink-0">Read</span>
            </button>

            <button
              data-testid="open-timer-card"
              onClick={() => navigate("/use/timer")}
              className="w-full bg-white rounded-3xl border-2 border-[#ED8936]/50 p-6 flex items-center gap-5 text-left transition-transform duration-200 ease-out hover:-translate-y-px"
            >
              <span className="w-20 h-20 rounded-2xl bg-[#ED8936]/10 grid place-items-center shrink-0">
                <Timer size={44} strokeWidth={2.5} className="text-[#ED8936]" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-2xl font-extrabold">Timer</span>
                <span className="block text-lg text-muted-foreground font-semibold">Watch time count down</span>
              </span>
              <span className="min-h-[56px] px-6 rounded-full bg-[#ED8936] text-white font-bold text-lg flex items-center shrink-0">Open</span>
            </button>

            {firstThen && (
              <button
                data-testid="open-first-then-card"
                onClick={() => navigate("/use/firstthen")}
                className="w-full bg-white rounded-3xl border-2 border-[#4299E1]/50 p-6 flex items-center gap-5 text-left transition-transform duration-200 ease-out hover:-translate-y-px"
              >
                <span className="w-20 h-20 rounded-2xl bg-[#4299E1]/10 grid place-items-center shrink-0">
                  <ArrowRight size={44} strokeWidth={2.5} className="text-[#4299E1]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-2xl font-extrabold truncate">First {firstThen.first_label}, then {firstThen.then_label}</span>
                  <span className="block text-lg text-muted-foreground font-semibold">One thing, then the next</span>
                </span>
                <span className="min-h-[56px] px-6 rounded-full bg-[#4299E1] text-white font-bold text-lg flex items-center shrink-0">Open</span>
              </button>
            )}

            <button
              data-testid="open-notes-card"
              onClick={() => navigate("/use/notes")}
              className="w-full bg-white rounded-3xl border-2 border-[#9F7AEA]/40 p-6 flex items-center gap-5 text-left transition-transform duration-200 ease-out hover:-translate-y-px"
            >
              <span className="w-20 h-20 rounded-2xl bg-[#9F7AEA]/10 grid place-items-center shrink-0">
                <FileText size={44} strokeWidth={2.5} className="text-[#9F7AEA]" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-2xl font-extrabold">My notes</span>
                <span className="block text-lg text-muted-foreground font-semibold">Updates from your carers</span>
              </span>
              <span className="min-h-[56px] px-6 rounded-full bg-[#9F7AEA] text-white font-bold text-lg flex items-center shrink-0">
                Open
              </span>
            </button>

            <button
              data-testid="open-plan-card"
              onClick={() => navigate("/use/plan")}
              className="w-full bg-white rounded-3xl border-2 border-[#38A169]/40 p-6 flex items-center gap-5 text-left transition-transform duration-200 ease-out hover:-translate-y-px"
            >
              <span className="w-20 h-20 rounded-2xl bg-[#38A169]/10 grid place-items-center shrink-0">
                <HeartHandshake size={44} strokeWidth={2.5} className="text-[#38A169]" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-2xl font-extrabold">My plan</span>
                <span className="block text-lg text-muted-foreground font-semibold">What your care plan says</span>
              </span>
              <span className="min-h-[56px] px-6 rounded-full bg-[#38A169] text-white font-bold text-lg flex items-center shrink-0">
                Open
              </span>
            </button>

            {choices.map((c) => (
              <button
                key={c.id}
                data-testid={`start-choice-${c.id}`}
                onClick={() => navigate(`/use/choice/${c.id}`)}
                className="w-full bg-white rounded-3xl border-2 border-[#ED8936]/50 p-6 flex items-center gap-5 text-left transition-transform duration-200 ease-out hover:-translate-y-px"
              >
                <span className="w-20 h-20 rounded-2xl bg-[#ED8936]/10 grid place-items-center shrink-0">
                  <ClipboardList size={44} strokeWidth={2.5} className="text-[#ED8936]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-2xl font-extrabold truncate">{c.name}</span>
                  <span className="block text-lg text-muted-foreground font-semibold">Tap to choose</span>
                </span>
                <span className="min-h-[56px] px-6 rounded-full bg-[#ED8936] text-white font-bold text-lg flex items-center shrink-0">
                  Open
                </span>
              </button>
            ))}

            {boards.length > 0 && (
              <button
                data-testid="open-board-card"
                onClick={() => navigate("/use/board")}
                className="w-full bg-white rounded-3xl border-2 border-[#9F7AEA]/50 p-6 flex items-center gap-5 text-left transition-transform duration-200 ease-out hover:-translate-y-px"
              >
                <span className="w-20 h-20 rounded-2xl bg-[#9F7AEA]/10 grid place-items-center shrink-0">
                  <LayoutGrid size={44} strokeWidth={2.5} className="text-[#9F7AEA]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-2xl font-extrabold truncate">{boards[0].name}</span>
                  <span className="block text-lg text-muted-foreground font-semibold">Tap pictures to talk</span>
                </span>
                <span className="min-h-[56px] px-6 rounded-full bg-[#9F7AEA] text-white font-bold text-lg flex items-center shrink-0">
                  Open
                </span>
              </button>
            )}

            {routines.length === 0 && boards.length === 0 && choices.length === 0 && (
              <p data-testid="nothing-set-up-message" className="bg-white rounded-3xl border-2 border-dashed border-foreground/20 p-8 text-lg text-muted-foreground">
                Nothing here yet. Ask your carer to set up a routine or talk board in Carer Mode.
              </p>
            )}
          </div>
        )}
      </main>
      <UserNav />
    </div>
  );
}
